import { useMutation, useQuery, useQueryClient } from "react-query";
import { apiCall } from "Apis/BaseApi";
import { IPaginatedList } from "Apis/Models/IPaginatedList";
import {IMutateOrderDto, IOrderDto, IOverviewOrderDto} from "Apis/Models/IOrderDto";
import {IQueryParams} from "./Models/IQueryParams";
import { saveAs } from 'file-saver';
import {ORDER_INVOICES_CACHE_KEY} from "./InvoicesApi";
import {IInvoiceDto} from "./Models/IInvoiceDto";

export const ORDERS_CACHE_KEY = 'ORDERS_CACHE_KEY '
export const useOrders = (queryable: Partial<IQueryParams> | null = null) => {
    return useQuery<IPaginatedList<IOverviewOrderDto>>([ORDERS_CACHE_KEY, queryable], async () => {
        return await apiCall<IPaginatedList<IOverviewOrderDto>>({
            url: `/orders`,
            method: 'GET',
            queryParams: queryable
        })
    });
}

export const useOrder = (id: number) => {
    return useQuery<IOrderDto>([ORDERS_CACHE_KEY, id], async () => {
        return await apiCall<IOrderDto>({
            url: `/orders/${id}`,
            method: 'GET',
        })
    });
}

export const useMutateOrder = () => {
    
    const queryClient = useQueryClient();
    
    return useMutation(async (order: IMutateOrderDto): Promise<IOrderDto> => {
        if (!order.id) {
            return await apiCall<IOrderDto>({
                url: `/orders`,
                method: 'POST',
                data: order
            })
        } else {
            return await apiCall<IOrderDto>({
                url: `/orders/${order.id}`,
                method: 'PUT',
                data: order
            })
        }
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useCreateInvoiceDraft = () => {
    const queryClient = useQueryClient();

    return useMutation(async (order: IOrderDto) => {
        return await apiCall<IInvoiceDto>({
            url: `/orders/${order.id}/invoice`,
            method: 'POST',
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDER_INVOICES_CACHE_KEY)
        }
    })
}

export const getOrderConfirmation = async (order: IOrderDto, fileName: string): Promise<any> => {
    
    const responseBlob = await apiCall<Blob>({
        responseType: 'blob',
        url: `/orders/${order.id}/orderConfirmation`,
        method: 'GET',
    });

    const blob = new Blob([responseBlob], {type: 'application/pdf' });

    saveAs(blob, fileName);
}

export const getOrderProductionNote = async (order: IOverviewOrderDto[], fileName: string, expanded: boolean = false): Promise<any> => {

    const queryParams = new URLSearchParams();

    order.forEach(x => queryParams.append('orderIds', x.id.toString()));

    queryParams.append('expanded', expanded.toString());

    const responseBlob = await apiCall<Blob>({
        responseType: 'blob',
        url: `/orders/productionNote?${queryParams.toString()}`,
        method: 'GET',
    });

    const blob = new Blob([responseBlob], {type: 'application/pdf' });

    saveAs(blob, fileName);
}

export const getOrderDeliveryNote = async (order: IOrderDto, fileName: string): Promise<any> => {

    const responseBlob = await apiCall<Blob>({
        responseType: 'blob',
        url: `/orders/${order.id}/deliverynote`,
        method: 'GET',
    });

    const blob = new Blob([responseBlob], {type: 'application/pdf' });

    saveAs(blob, fileName);
}